import { type PlaceState, applyUpdate } from './state.ts';

/* The data adapter: the manifest and live state from the platform API. The first frame never waits for
 * live state, and a dropped connection falls back to a fresh snapshot. */

export interface Manifest {
  mode: 'public' | 'signed_in';
  building: { id: string; code: string; name: string; timeZone: string };
  model: { url: string; contentHash: string; sizeBytes: number; version: number } | null;
  scene: Record<string, unknown>;
  places: { code: string; name: string; kind: string; floorNumber: number | null; nodeName: string | null }[];
}

/** The manifest request failed: `status` is the HTTP status, or 0 when no response arrived. */
export class ManifestError extends Error {
  readonly status: number;
  constructor(status: number, cause?: unknown) {
    super(status === 0 ? 'manifest: no response' : `manifest: ${status}`, { cause });
    this.name = 'ManifestError';
    this.status = status;
  }

  /** A failure on our side (5xx), as opposed to the caller's (4xx) or the network's (no response). */
  get serverFault(): boolean {
    return this.status >= 500;
  }
}

export async function fetchManifest(
  apiUrl: string,
  buildingCode: string,
  token: string | null,
): Promise<Manifest> {
  // A signed-in read skips the HTTP cache: the anonymous manifest is cacheable for a minute under the same
  // URL (apps/api/src/modules/twin/routes.ts), and would keep a visitor who signs in on the same tab on the
  // public view (regression B2).
  let response: Response;
  try {
    response = await fetch(`${apiUrl}/v1/twin/${encodeURIComponent(buildingCode)}/manifest`, {
      cache: token ? 'no-store' : 'default',
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
  } catch (error) {
    throw new ManifestError(0, error); // offline, DNS, CORS: the request never got an answer
  }
  if (!response.ok) throw new ManifestError(response.status);
  return (await response.json()) as Manifest;
}

/** Opens the live channel; calls onState with every place that changed. Returns a close function. */
export function connectLive(
  apiUrl: string,
  buildingCode: string,
  token: string,
  onState: (places: Map<string, PlaceState>, changed: string[]) => void,
): () => void {
  let state = { sequence: 0, places: new Map<string, PlaceState>() };
  let socket: WebSocket | null = null;
  let closed = false;
  let retryMs = 1_000;

  const open = (): void => {
    const url = new URL(`/v1/twin/${encodeURIComponent(buildingCode)}/live`, apiUrl);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    socket = new WebSocket(url);
    socket.addEventListener('open', () => socket?.send(JSON.stringify({ type: 'auth', token })));
    socket.addEventListener('message', (event) => {
      const message = JSON.parse(String(event.data)) as {
        type: string;
        sequence: number;
        places: PlaceState[];
      };
      if (message.type === 'snapshot') state = { sequence: 0, places: new Map() };
      if (message.type !== 'snapshot' && message.type !== 'delta') return;
      retryMs = 1_000;
      const next = applyUpdate(state, message);
      state = { sequence: next.sequence, places: next.places };
      onState(state.places, next.changed);
    });
    socket.addEventListener('close', () => {
      if (closed) return;
      setTimeout(open, retryMs);
      retryMs = Math.min(retryMs * 2, 30_000);
    });
  };
  open();
  return () => {
    closed = true;
    socket?.close();
  };
}
